import { useCallback, use, useMemo, useRef } from 'react';
import { useStore } from 'zustand';

import { LbContext } from '../model/store';
import { PollingService } from '../services/PollingService';
import type { BasePollingService } from '../types';

import { useOptions } from './useOptions';

export const usePosts = () => {
  const store = use(LbContext);
  const options = useOptions();
  const { refreshRate, transport, hideNewPosts } = options;
  const allPosts = useStore(store, (state) => state.items);
  const posts = useMemo(() => allPosts.filter((p) => !hideNewPosts || !p.isNew), [allPosts, hideNewPosts]);
  const newPosts = useMemo(() => allPosts.filter((p) => hideNewPosts && !!p.isNew), [allPosts, hideNewPosts]);
  const serviceInstance = useRef<BasePollingService>(transport?.pollingService ?? new PollingService(options));
  const status = useStore(store, (state) => state.status);
  const loadPostsAction = useStore(store, (state) => state.loadPosts);
  const loadUpdatesAction = useStore(store, (state) => state.loadUpdates);
  const showAddedPostsAction = useStore(store, (state) => state.showAddedPosts);

  const polling = useRef<NodeJS.Timeout>(null);

  const loadPosts = useCallback(async () => {
    await loadPostsAction(serviceInstance.current);
  }, [loadPostsAction]);

  const stopPolling = useCallback(() => {
    if (polling.current) {
      clearInterval(polling.current);
      polling.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (refreshRate <= 0) return;

    stopPolling();
    polling.current = setInterval(() => {
      loadUpdatesAction(serviceInstance.current);
    }, refreshRate);
  }, [loadUpdatesAction, refreshRate, stopPolling]);

  const showAddedPosts = useCallback(() => {
    showAddedPostsAction();
  }, [showAddedPostsAction]);

  return { loadPosts, startPolling, stopPolling, status, posts, newPosts, showAddedPosts };
};
